<template>
  <div>
    <div class="d-flex align-items-center justify-content-between flex-wrap mb-2">
      <div>
        <h1 class="font-weight-bolder mb-0">
          {{ $t('materialSuggestions') }}
        </h1>
        <small class="text-muted">{{ $t('materialSuggestionsHint') }}</small>
      </div>
    </div>

    <b-card no-body>
      <div class="ms-toolbar">
        <b-form-radio-group
          v-model="status"
          :options="statusOptions"
          button-variant="outline-primary"
          size="sm"
          buttons
          @change="load(1)"
        />
        <b-form-input
          v-model="search"
          :placeholder="$t('search')"
          class="ms-toolbar__search"
          debounce="400"
          @update="load(1)"
        />
      </div>

      <b-table
        :items="rows"
        :fields="fields"
        :busy="loading"
        responsive
        show-empty
        :empty-text="$t('materialSuggestionsEmpty')"
        class="mb-0"
      >
        <template #table-busy>
          <div class="text-center p-2">
            <b-spinner
              small
              class="mr-50"
            />{{ $t('loading') }}
          </div>
        </template>

        <template #cell(name)="{ item }">
          <div class="font-weight-bolder">
            {{ item.name }}
          </div>
          <small class="text-muted">
            <span v-if="item.brand">{{ item.brand }}</span>
            <span v-if="item.brand && item.description"> · </span>
            <span v-if="item.description">{{ item.description }}</span>
          </small>
        </template>

        <template #cell(unit_type)="{ value }">
          {{ unitLabel(value) }}
        </template>

        <template #cell(customer)="{ item }">
          <div>{{ item.customer && item.customer.name }}</div>
          <small class="text-muted">{{ item.project && item.project.name }}</small>
        </template>

        <template #cell(occurrences)="{ value }">
          <b-badge
            :variant="value > 1 ? 'light-warning' : 'light-secondary'"
            pill
          >
            {{ value }}
          </b-badge>
        </template>

        <template #cell(created_at)="{ value }">
          {{ value | date }}
        </template>

        <template #cell(status)="{ item }">
          <b-badge :variant="statusVariant(item.status)">
            {{ $t(`materialSuggestionStatus.${item.status}`) }}
          </b-badge>
          <div
            v-if="item.product_catalog"
            class="mt-25"
          >
            <small class="text-muted">{{ item.product_catalog.sku }} · {{ item.product_catalog.name }}</small>
          </div>
          <div
            v-if="item.review_note"
            class="mt-25"
          >
            <small class="text-muted">“{{ item.review_note }}”</small>
          </div>
        </template>

        <template #cell(actions)="{ item }">
          <div
            v-if="item.status === 'pending'"
            class="ms-actions"
          >
            <b-button
              size="sm"
              variant="primary"
              @click="openApprove(item)"
            >
              {{ $t('approve') }}
            </b-button>
            <b-button
              size="sm"
              variant="outline-primary"
              @click="openLink(item)"
            >
              {{ $t('materialSuggestionLink') }}
            </b-button>
            <b-button
              size="sm"
              variant="outline-danger"
              @click="openReject(item)"
            >
              {{ $t('reject') }}
            </b-button>
          </div>
        </template>
      </b-table>

      <div
        v-if="pagination.total_pages > 1"
        class="d-flex justify-content-end p-1"
      >
        <b-pagination
          v-model="page"
          :total-rows="pagination.total_objects"
          :per-page="perPage"
          @change="load"
        />
      </div>
    </b-card>

    <b-modal
      id="msApprove"
      :title="$t('materialSuggestionApproveTitle')"
      :ok-title="$t('materialSuggestionApproveOk')"
      :cancel-title="$t('cancel')"
      :ok-disabled="!approveValid || working"
      centered
      @ok.prevent="approve"
    >
      <p class="text-muted small mb-1">
        {{ $t('materialSuggestionApproveHint') }}
      </p>
      <b-form-group :label="$t('name')">
        <b-form-input v-model="approveForm.name" />
      </b-form-group>
      <b-row>
        <b-col sm="6">
          <b-form-group label="SKU">
            <b-form-input v-model="approveForm.sku" />
          </b-form-group>
        </b-col>
        <b-col sm="6">
          <b-form-group :label="$t('unit')">
            <b-form-select
              v-model="approveForm.unit_type"
              :options="unitOptions"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group :label="$t('category')">
        <b-form-select
          v-model="approveForm.category_id"
          :options="categoryOptions"
        />
      </b-form-group>
      <b-form-group :label="$t('brand')">
        <b-form-input v-model="approveForm.brand" />
      </b-form-group>
      <b-form-group
        :label="$t('description')"
        class="mb-0"
      >
        <b-form-input v-model="approveForm.description" />
      </b-form-group>
    </b-modal>

    <b-modal
      id="msLink"
      :title="$t('materialSuggestionLinkTitle')"
      :ok-title="$t('materialSuggestionLinkOk')"
      :cancel-title="$t('cancel')"
      :ok-disabled="!linkForm.product || working"
      centered
      @ok.prevent="link"
    >
      <p class="text-muted small mb-1">
        {{ $t('materialSuggestionLinkHint', { name: current && current.name }) }}
      </p>
      <b-form-group :label="$t('product')">
        <vue-bootstrap-typeahead
          v-model="linkForm.query"
          :placeholder="$t('expenseCapture.searchMaterial')"
          :data="linkForm.results"
          :serializer="item => `${item.sku} · ${item.name}`"
          :show-all-results="true"
          :max-matches="8"
          @hit="linkForm.product = $event"
        />
      </b-form-group>
      <div
        v-if="linkForm.product"
        class="ms-picked"
      >
        <feather-icon
          icon="CheckIcon"
          size="14"
          class="mr-25"
        />{{ linkForm.product.sku }} · {{ linkForm.product.name }}
      </div>
      <b-form-group
        :label="$t('note')"
        class="mb-0 mt-1"
      >
        <b-form-input v-model="linkForm.note" />
      </b-form-group>
    </b-modal>

    <b-modal
      id="msReject"
      :title="$t('materialSuggestionRejectTitle')"
      :ok-title="$t('reject')"
      ok-variant="danger"
      :cancel-title="$t('cancel')"
      :ok-disabled="working"
      centered
      @ok.prevent="reject"
    >
      <p class="mb-1">
        {{ $t('materialSuggestionRejectHint', { name: current && current.name }) }}
      </p>
      <b-form-group
        :label="$t('note')"
        class="mb-0"
      >
        <b-form-input
          v-model="rejectForm.note"
          :placeholder="$t('materialSuggestionRejectPlaceholder')"
        />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import _ from 'underscore'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const UNIT_KEYS = ['piece', 'bundle', 'box', 'kilogram', 'ton', 'meter', 'square_meters', 'cubic_meters', 'trip']

export default {
  data() {
    return {
      loading: false,
      working: false,
      rows: [],
      pagination: { total_pages: 0, total_objects: 0 },
      page: 1,
      perPage: 25,
      status: 'pending',
      search: '',
      current: null,
      approveForm: {
        name: '', sku: '', unit_type: null, category_id: null, brand: '', description: '',
      },
      linkForm: {
        query: '', results: [], product: null, note: '',
      },
      rejectForm: { note: '' },
    }
  },

  computed: {
    ...mapGetters('app', ['unitsFilter']),
    ...mapGetters('categories', ['categories']),

    fields() {
      return [
        { key: 'name', label: this.$t('material') },
        { key: 'unit_type', label: this.$t('unit') },
        { key: 'customer', label: this.$t('customer') },
        { key: 'occurrences', label: this.$t('materialSuggestionTimes'), class: 'text-center' },
        { key: 'created_at', label: this.$t('date') },
        { key: 'status', label: this.$t('status') },
        { key: 'actions', label: '', class: 'text-right' },
      ]
    },

    statusOptions() {
      return ['pending', 'approved', 'linked', 'rejected'].map(value => ({
        value,
        text: this.$t(`materialSuggestionStatus.${value}`),
      }))
    },

    unitOptions() {
      return [
        { value: null, text: this.$t('expenseCapture.pickUnit') },
        ...UNIT_KEYS.map(value => ({ value, text: this.$tc(this.unitsFilter(value), 2) })),
      ]
    },

    categoryOptions() {
      const list = Array.isArray(this.categories) ? this.categories : []
      return [
        { value: null, text: this.$t('materialSuggestionPickCategory') },
        ...list.map(c => ({ value: c.id, text: c.name })),
      ]
    },

    approveValid() {
      return this.approveForm.name.trim().length > 0
        && this.approveForm.sku.trim().length > 0
        && Boolean(this.approveForm.unit_type)
        && Boolean(this.approveForm.category_id)
    },
  },

  watch: {
    'linkForm.query': function onQuery(value) {
      if (this.linkForm.product && value !== `${this.linkForm.product.sku} · ${this.linkForm.product.name}`) {
        this.linkForm.product = null
      }
      this.searchProducts(value)
    },
  },

  created() {
    this.load(1)
    this.fetchCategories()
  },

  methods: {
    ...mapActions('materialSuggestions', [
      'fetchMaterialSuggestions', 'approveMaterialSuggestion', 'linkMaterialSuggestion', 'rejectMaterialSuggestion',
    ]),
    ...mapActions('categories', ['fetchCategories']),
    ...mapActions('products', ['fetchProducts']),

    async load(page = this.page) {
      this.page = page
      this.loading = true
      try {
        const data = await this.fetchMaterialSuggestions({
          status: this.status, name: this.search, page, perPage: this.perPage,
        })
        this.rows = data?.data || []
        this.pagination = data?.meta?.pagination || { total_pages: 0, total_objects: 0 }
      } finally {
        this.loading = false
      }
    },

    unitLabel(value) {
      const key = this.unitsFilter(value)
      return key ? this.$tc(key, 2) : '—'
    },

    statusVariant(status) {
      return {
        pending: 'light-warning', approved: 'light-success', linked: 'light-info', rejected: 'light-secondary',
      }[status] || 'light-secondary'
    },

    suggestSku(name) {
      const base = String(name || '')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 24)
      return base ? `${base}-${Date.now().toString().slice(-4)}` : ''
    },

    openApprove(item) {
      this.current = item
      this.approveForm = {
        name: item.name,
        sku: this.suggestSku(item.name),
        unit_type: item.unit_type || null,
        category_id: null,
        brand: item.brand || '',
        description: item.description || '',
      }
      this.$bvModal.show('msApprove')
    },

    async approve() {
      if (!this.approveValid) return
      this.working = true
      try {
        await this.approveMaterialSuggestion({ id: this.current.id, product: this.approveForm })
        this.$bvModal.hide('msApprove')
        this.toast(this.$t('materialSuggestionApproved', { name: this.approveForm.name }), 'success')
        await this.load()
      } finally {
        this.working = false
      }
    },

    openLink(item) {
      this.current = item
      this.linkForm = {
        query: '', results: [], product: null, note: '',
      }
      this.$bvModal.show('msLink')
    },

    searchProducts: _.debounce(function search(query) {
      if (!query || query.trim().length < 2) {
        this.linkForm.results = []
        return
      }
      this.fetchProducts({ by_name: query, by_active_status: true, meta: { pagination: { per_page: 10 } } })
        .then(response => { this.linkForm.results = JSON.parse(JSON.stringify(response.data)) })
    }, 400),

    async link() {
      if (!this.linkForm.product) return
      this.working = true
      try {
        await this.linkMaterialSuggestion({
          id: this.current.id, productCatalogId: this.linkForm.product.id, note: this.linkForm.note,
        })
        this.$bvModal.hide('msLink')
        this.toast(this.$t('materialSuggestionLinked', { name: this.linkForm.product.name }), 'success')
        await this.load()
      } finally {
        this.working = false
      }
    },

    openReject(item) {
      this.current = item
      this.rejectForm = { note: '' }
      this.$bvModal.show('msReject')
    },

    async reject() {
      this.working = true
      try {
        await this.rejectMaterialSuggestion({ id: this.current.id, note: this.rejectForm.note })
        this.$bvModal.hide('msReject')
        this.toast(this.$t('materialSuggestionRejected'), 'warning')
        await this.load()
      } finally {
        this.working = false
      }
    },

    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: 'PackageIcon', variant },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.ms-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #ebe9f1;
}

.ms-toolbar__search {
  max-width: 20rem;
}

.ms-actions {
  display: inline-flex;
  gap: 0.4rem;
  white-space: nowrap;
}

.ms-picked {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.6rem;
  border-radius: 0.4rem;
  background: rgba(31, 157, 99, 0.1);
  color: #1f9d63;
  font-size: 0.85rem;
  font-weight: 600;
}
</style>
